package com.iregained.site.components.sections.patients.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.patients.styles.ProtocolCardBottomAccentStyle
import com.iregained.site.components.sections.patients.styles.ProtocolCardStyle
import com.iregained.site.components.sections.patients.styles.ProtocolImageStyle
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.theme.SubheadlineTextStyle
import com.iregained.site.theme.toSitePalette
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.dom.Div

// UNIQUE ANIMATION: 3D Flip-In
@Composable
private fun Modifier.flipIn3D(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0).rotateX(if (isVisible) 0.deg else 90.deg)
        .styleModifier { property("transform-origin", "top center"); property("perspective", "1000px") }.transition(
            Transition.of("opacity", 0.8.s, TransitionTimingFunction.EaseOut, delayMs.ms),
            Transition.of("transform", 0.8.s, TransitionTimingFunction.EaseOut, delayMs.ms)
        )
}

@Composable
fun PatientsRecoveryProtocolsSection(data: PatientsRecoveryProtocolsSectionData = defaultPatientsRecoveryProtocolsSectionData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val palette = ColorMode.current.toSitePalette()
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "patients-recovery-protocols"
    var isVisible by remember { mutableStateOf(false) }

    if (data.protocols.isNotEmpty()) {
        Box(
            modifier = Modifier.id(targetId).fillMaxWidth().backgroundColor(palette.brand.primarySoft)
                .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent)
                .styleModifier { property("scroll-margin-top", "100px") }, contentAlignment = Alignment.Center
        ) {
            Div(
                attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex).justifyContent(JustifyContent.Center)
                    .toAttrs {
                        ref { element ->
                            val observer = IntersectionObserver { entries, obs ->
                                entries.forEach {
                                    if (it.isIntersecting) {
                                        isVisible = true; obs.unobserve(element)
                                    }
                                }
                            }
                            observer.observe(element); onDispose { observer.disconnect() }
                        }
                    }) {
                Column(
                    modifier = Modifier.fillMaxWidth().maxWidth(1440.px),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    if (data.headline.isNotBlank()) {
                        SectionHeader(
                            text = data.headline,
                            fontSize = 32.px,
                            lineWidthPercent = 25.percent,
                            modifier = Modifier.margin(bottom = 16.px).opacity(if (isVisible) 1 else 0)
                                .transition(Transition.of("opacity", 0.8.s)),
                            alignment = Alignment.CenterHorizontally
                        )
                    }

                    if (data.description.isNotBlank()) {
                        Div(
                            attrs = SubheadlineTextStyle.toModifier().maxWidth(1200.px).fontSize(18.px)
                                .fontWeight(FontWeight.Medium).color(palette.custom.darkText)
                                .textAlign(TextAlign.Center).margin(bottom = 40.px).opacity(if (isVisible) 1 else 0)
                                .transition(Transition.of("opacity", 0.8.s, delay = 200.ms)).toAttrs {
                                    ref { htmlElement ->
                                        htmlElement.innerHTML = data.description; onDispose {}
                                    }
                                })
                    }

                    SimpleGrid(
                        numColumns = numColumns(base = 1, sm = 2, lg = 4),
                        modifier = Modifier.fillMaxWidth().maxWidth(1440.px).gap(40.px).margin(bottom = 25.px)
                    ) {
                        data.protocols.forEachIndexed { index, protocol ->
                            key(protocol.title) {
                                ProtocolCard(protocol, palette, Modifier.flipIn3D(isVisible, 300 + (index * 150)))
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun ProtocolCard(protocol: ProtocolItem, palette: com.iregained.site.theme.SitePalette, modifier: Modifier) {
    Box(
        modifier = ProtocolCardStyle.toModifier().then(modifier).fillMaxWidth().display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Column)
    ) {
        if (protocol.visualPath.isNotBlank()) {
            Box(
                modifier = ProtocolImageStyle.toModifier().fillMaxWidth().height(200.px).overflow(Overflow.Hidden)
            ) {
                Image(
                    src = protocol.visualPath,
                    alt = protocol.title,
                    modifier = Modifier.fillMaxSize().objectFit(ObjectFit.Cover)
                )
            }
        }
        Column(modifier = Modifier.padding(30.px)) {
            if (protocol.title.isNotBlank()) SpanText(
                text = protocol.title,
                modifier = Modifier.fontSize(22.px).fontWeight(FontWeight.Bold).color(palette.custom.darkText)
                    .margin(bottom = 12.px).lineHeight(1.3)
            )
            if (protocol.description.isNotBlank()) Div(
                attrs = Modifier.fontSize(16.px).color(palette.custom.darkText).opacity(85.percent).lineHeight(1.6)
                    .toAttrs { ref { htmlElement -> htmlElement.innerHTML = protocol.description; onDispose {} } })
        }
        Box(modifier = ProtocolCardBottomAccentStyle.toModifier())
    }
}

// ==========================================
// DATA MODELS & CMS IMAGE SPECS
// ==========================================
data class ProtocolItem(
    val title: String, val description: String, val visualPath: String
)

/**
 * CMS IMAGE UPLOAD SPECS FOR RECOVERY PROTOCOLS:
 * @field visualPath (inside ProtocolItem):
 * - Aspect Ratio: 16:9 (Widescreen).
 * - Recommended Dimensions: 600x338px.
 * - Format: Short, looping GIF of the hand performing the specific motion (highly recommended) or high-quality WebP/JPEG.
 * - Content: Focus specifically on the hand and the device mechanism moving.
 */
data class PatientsRecoveryProtocolsSectionData(
    val sectionId: String? = null, val headline: String, val description: String, val protocols: List<ProtocolItem>
)

val defaultPatientsRecoveryProtocolsSectionData = PatientsRecoveryProtocolsSectionData(
    sectionId = "patients-recovery-protocols",
    headline = "TARGETED PROTOCOLS FOR FUNCTIONAL RECOVERY",
    description = "The MyHand<sup>®</sup> System guides your through specialized evidence-based, neuroplasticity-driven protocols to rebuild strength and improve precision and coordination, allowing you to independently perform Activities of Daily Living (ADLs).",
    protocols = listOf(
        ProtocolItem(
            title = "Active Grasp & Release",
            description = "Focuses on the foundational mechanics of opening and closing the hand, vital for holding everyday objects like cups or utensils.",
            // Placeholder path - Update in AppConstants.kt
            visualPath = Res.Images.PatientsPage.Recovery.ACTIVE_GRASP
        ), ProtocolItem(
            title = "Independent Finger Movement    ",
            description = "Targets the ability to move each finger independently, rebuilding the fine motor skills required for typing, buttoning shirts, or managing keys.",
            visualPath = Res.Images.PatientsPage.Recovery.FINGER_INDIVIDUATION
        ), ProtocolItem(
            title = "Pinch Grasp",
            description = "Concentrates on the thumb and index finger coordination, essential for picking up small items, writing, and intricate daily tasks.",
            visualPath = Res.Images.PatientsPage.Recovery.PINCH_STRENGTH
        ), ProtocolItem(
            title = "Functional Task Simulation",
            description = "Combines multiple movements into game scenarios that mimic real-world activities, bridging the gap between therapy and normal life.",
            visualPath = Res.Images.PatientsPage.Recovery.TASK_SIMULATION
        ), ProtocolItem(
            title = "Real-Time Force & Motion Tracking",
            description = "Provides real-time measurement of force and range of motion directly in the fingers, allowing for precise quantification of joint flexibility and gripping strength.",
            visualPath = Res.Images.PatientsPage.Recovery.FORCE_ROM
        ), ProtocolItem(
            title = "Real-Time Biofeedback",
            description = "Delivers real-time visual cues and sensory data, helping patients refine movement patterns and adjust muscle engagement instantly.",
            visualPath = Res.Images.PatientsPage.Recovery.BIO_FEEDBACK
        ), ProtocolItem(
            title = "Intrinsic Muscle Strength",
            description = "Targets the small muscles within the palm essential for fine coordination, hand stability, and arch integrity.",
            visualPath = Res.Images.PatientsPage.Recovery.INTRINSIC_MUSCLE
        ), ProtocolItem(
            title = "Interactive Gamification",
            description = "Transforms repetitive therapeutic exercises into motivating challenges and interactive games, boosting patient adherence and enjoyment.",
            visualPath = Res.Images.PatientsPage.Recovery.GAMIFIED_ENGAGEMENT
        )
    )
)